import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../../components/ui/select';
import { Settings, Wrench, Code, AlertTriangle, Edit3, Table2 } from 'lucide-react';
import JobParameters from './JobParameters';
import PassParametersTable from './PassParametersTable';
import WeldParametersTable from './WeldParametersTable';
import ErrorDisplay from './ErrorDisplay';
import RenamePass from './RenamePass';

interface P600zParametersProps {
    jobData?: any[];
    projectId?: number;
    onRenameSuccess?: () => void;
    canEdit?: boolean;
}

interface JobParameter {
    job_number: string;
    parsed_json_data?: any[];
    is_renamed?: boolean;
    [key: string]: any;
}

const P600zParameters: React.FC<P600zParametersProps> = ({ 
    jobData,
    projectId,
    onRenameSuccess,
    canEdit = true,
}) => {
    const [activeTab, setActiveTab] = useState<string>('job-parameters');
    const [selectedJob, setSelectedJob] = useState<string>('');
    
    // Extract job numbers from the array
    const jobNumbers = useMemo(() => {
        if (!jobData || !Array.isArray(jobData)) return [];
        return jobData.map((item: JobParameter) => item.job_number).filter(Boolean);
    }, [jobData]);

    // Set first job as default
    useEffect(() => {
        if (jobNumbers.length > 0 && !selectedJob) {
            setSelectedJob(jobNumbers[0]);
        }
    }, [jobNumbers, selectedJob]);

    // Get selected job data
    const selectedJobData = useMemo(() => {
        if (!selectedJob || !jobData) return null;
        return jobData.find((item: JobParameter) => item.job_number === selectedJob);
    }, [selectedJob, jobData]);

    const handleJobSelect = (jobNum: string) => {
        setSelectedJob(jobNum);
    };

    // Check if there are errors for the selected job
    const hasErrors = useMemo(() => {
        if (!selectedJobData) return false;
        
        // Check if there are error indicators in the job data
        // Check for weldParameterErrors or parsedErrors in the job data
        if (selectedJobData.weldParameterErrors || selectedJobData.parsedErrors) {
            const errors = selectedJobData.weldParameterErrors || selectedJobData.parsedErrors;
            
            // Check if there are actual errors
            if (errors.errorMessages && errors.errorMessages.length > 0) return true;
            if (errors.invalidPassZoneMapping && errors.invalidPassZoneMapping.length > 0) return true;
            if (errors.invalidLimits && errors.invalidLimits.length > 0) return true;
            if (errors.hasInvalidPassName || errors.hasInvalidZoneName || errors.hasNumericPassError) return true;
        }
        
        // Check if there's a weldXMLErrors property with content
        if (selectedJobData.weldXMLErrors && selectedJobData.weldXMLErrors.trim() !== '') {
            return true;
        }
        
        return false;
    }, [selectedJobData]);

    // Check if the job needs renaming (is_renamed = false)
    const needsRenaming = useMemo(() => {
        return true;
        if (!selectedJobData) return false;
        return selectedJobData.is_renamed === false;
    }, [selectedJobData]);

    return (
        <div className="h-full flex flex-col">
            {/* Job Selection — only if multiple jobs */}
            {jobNumbers.length > 1 && (
                <div className="px-6 pt-4 pb-2 bg-white border-b border-[#E2E5EA]">
                    <div className="flex items-center gap-3">
                        <label className="text-[12px] font-medium text-text-grey whitespace-nowrap">
                            Select Machine/Job
                        </label>
                        <Select value={selectedJob} onValueChange={handleJobSelect}>
                            <SelectTrigger className="w-[260px] h-9">
                                <SelectValue placeholder="Choose a job number..." />
                            </SelectTrigger>
                            <SelectContent>
                                {jobNumbers.map((jobNumber) => (
                                    <SelectItem key={jobNumber} value={jobNumber}>
                                        P600z: {jobNumber}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="bg-white border-b border-[#E2E5EA] px-6">
                    <TabsList className="h-auto w-full justify-start gap-6 rounded-none bg-transparent p-0">
                        <TabsTrigger
                            value="job-parameters"
                            className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            <div className="flex items-center gap-2">
                                <Settings className="h-4 w-4" />
                                Job Parameters
                            </div>
                        </TabsTrigger>
                        <TabsTrigger
                            value="weld-parameters"
                            className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            <div className="flex items-center gap-2">
                                <Table2 className="h-4 w-4" />
                                Weld Parameters
                            </div>
                        </TabsTrigger>
                        {hasErrors && (
                            <TabsTrigger
                                value="errors"
                                className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                            >
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4" />
                                    Errors
                                    <span className="ml-1 px-1.5 py-0.5 text-[10px] text-readable-red bg-[rgba(196,0,31,0.06)] rounded-full">
                                        !
                                    </span>
                                </div>
                            </TabsTrigger>
                        )}
                        {needsRenaming && canEdit && (
                            <TabsTrigger
                                value="rename-pass"
                                className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                            >
                                <div className="flex items-center gap-2">
                                    <Edit3 className="h-4 w-4" />
                                    Rename Pass
                                </div>
                            </TabsTrigger>
                        )}
                    </TabsList>
                </div>

                <div className="flex-1 min-h-0 overflow-hidden bg-page-bg">
                    <TabsContent value="job-parameters" className="space-y-4 mt-0 h-full overflow-auto" >
                        {selectedJobData ? (
                            <div className="flex-1 min-h-0 overflow-auto">
                            <JobParameters jobData={selectedJobData} />
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <Settings className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">All Configurations</h3>
                                <p>Please select a job to view job parameters</p>
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="weld-parameters" className="mt-0 h-full overflow-auto data-[state=inactive]:hidden" 
                    >
                        {selectedJobData ? (
                              <div className="min-h-0">
                            <WeldParametersTable jobData={selectedJobData} />
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <Table2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Weld Parameters</h3>
                                <p>Please select a job to view weld parameters</p>
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="pass-parameters" className="space-y-4 mt-0 h-full overflow-hidden" >
                        {selectedJobData ? (
                            <div className="flex-1 min-h-0 overflow-auto">
                            <PassParametersTable jobData={selectedJobData} />
                            
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <Wrench className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Pass Parameters</h3>
                                <p>Please select a job to view pass parameters</p>
                            </div>
                        )}
                    </TabsContent>

                    {hasErrors && <TabsContent value="errors" className="space-y-4 mt-0 h-full overflow-hidden">
                        {selectedJobData ? (
                            <div className="flex-1 min-h-0 overflow-hidden">
                            <ErrorDisplay jobData={selectedJobData} />
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Information</h3>
                                <p>Please select a job to view error information</p>
                            </div>
                        )}
                    </TabsContent>}

                    {needsRenaming && canEdit && (
                        <TabsContent value="rename-pass" className="space-y-4 mt-0 h-full overflow-auto flex flex-col" 
                      >
                            {selectedJobData ? (
                                    <div className="flex-1 min-h-0 overflow-hidden">
                                    <RenamePass jobData={selectedJobData} projectId={projectId} onRenameSuccess={onRenameSuccess} />
                                    </div>
                                
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <Edit3 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Rename Pass</h3>
                                    <p>Please select a job to rename passes</p>
                                </div>
                            )}
                        </TabsContent>
                    )}
                </div>
            </Tabs>
        </div>
    );
};

export default P600zParameters;
